/**
 * Onboarding routing decisions — PURE functions (no Angular / no DI / no
 * side-effects), extracted from `onboarding.guard.ts` so the full routing
 * matrix can be exhaustively unit-tested without TestBed or `toObservable`
 * plumbing.
 *
 * The guards stay responsible for: reading the signals, computing the boolean
 * inputs below, performing side-effects (clearing sentinels), and turning a
 * decision into a `UrlTree`. The *decision* itself lives here.
 *
 * A decision is either `true` (allow the navigation) or an array of route
 * segments to redirect to (fed to `router.createUrlTree(...)`).
 */

import {
  WorkspaceType} from '@shared/definitions/workspace';
import {
  ownerLikeWorkspaceType,
} from '@shared/definitions/workspace';

/** `true` = allow; `string[]` = redirect to these route segments. */
export type OnboardingDecision = true | string[];

export interface OnboardingRoutingInput {
  /** Active workspace type (defaulted to 'personal' by the caller if null). */
  type: WorkspaceType;
  /**
   * Whether the per-human user profile is done — i.e.
   * `user.profile.completedAt` OR the session "just finished" sentinel.
   */
  userProfileCompleted: boolean;
  /** Whether the requested URL is one of the user-level onboarding steps. */
  onUserStep: boolean;
  /** `?relaunch=true` escape hatch (settings "relaunch tour"). */
  relaunch: boolean;
  /** Whether `workspace.onboarding.completed` is true. */
  workspaceOnboardingCompleted: boolean;
  /** Whether the workspace-level "just finished" sentinel is set. */
  onboardingJustFinished: boolean;
}

/**
 * Decision for `OnboardingAccessGuard` (protects `/onboarding/*`).
 *
 * Order matters and mirrors the guard exactly:
 *  - service_provider / tenant have dedicated flows;
 *  - agency shares the universal USER onboarding, then hands off to its own
 *    wizard (NEVER the generic owner/property-manager workspace flow);
 *  - any other non-owner-like type doesn't use this flow;
 *  - owner-like types run user onboarding first, then the workspace steps.
 */
export function resolveOnboardingAccess(i: OnboardingRoutingInput): OnboardingDecision {
  if (i.type === 'service_provider') {
    return ['/provider/onboarding'];
  }
  if (i.type === 'tenant') {
    return ['/tenant/dashboard'];
  }

  if (i.type === 'agency') {
    if (!i.userProfileCompleted && !i.relaunch) {
      return i.onUserStep ? true : ['/onboarding', 'user-profile'];
    }
    return ['/agency/onboarding'];
  }

  // Syndic: same universal USER steps as agency (profile + consent), then
  // the syndic dashboard — never the owner welcome/setup tunnel (#591).
  if (i.type === 'syndic') {
    if (!i.userProfileCompleted && !i.relaunch) {
      return i.onUserStep ? true : ['/onboarding', 'user-profile'];
    }
    return ['/syndic/dashboard'];
  }

  if (!ownerLikeWorkspaceType(i.type)) {
    return ['/'];
  }

  // User-level onboarding always runs first for owner-like types.
  if (!i.userProfileCompleted && !i.relaunch) {
    return i.onUserStep ? true : ['/onboarding', 'user-profile'];
  }

  // User-level done: a stale URL on a user-* step is bounced forward.
  if (i.userProfileCompleted && i.onUserStep && !i.relaunch) {
    return i.workspaceOnboardingCompleted ? ['/'] : ['/onboarding', 'welcome'];
  }

  if (i.workspaceOnboardingCompleted && !i.relaunch) {
    return ['/'];
  }
  if (i.onboardingJustFinished && !i.relaunch) {
    return ['/'];
  }
  return true;
}

/**
 * Decision for `OnboardingCompletedGuard` (protects the main app shell `/`).
 *
 * Owner-like types AND agencies must finish the universal user-level
 * onboarding before reaching the app. Owner-like types must also finish their
 * workspace onboarding. Other types (service_provider/syndic/tenant) have their
 * own flows and are not gated here.
 */
export function resolveOnboardingCompleted(i: OnboardingRoutingInput): OnboardingDecision {
  const needsUserOnboarding =
    (ownerLikeWorkspaceType(i.type) || i.type === 'agency' || i.type === 'syndic') &&
    !i.userProfileCompleted;

  if (needsUserOnboarding && !i.onboardingJustFinished) {
    return ['/onboarding', 'user-profile'];
  }

  // Agencies must finish their dedicated (full-screen, menu-less) wizard before
  // reaching the app shell. Completion is mirrored onto
  // `workspace.onboarding.completed` by `completeAgencyOnboarding`.
  if (i.type === 'agency') {
    if (i.workspaceOnboardingCompleted || i.onboardingJustFinished) {
      return true;
    }
    return ['/agency/onboarding'];
  }

  // Syndic: user onboarding only — no workspace wizard. Once the personal
  // block is done they may enter the syndic shell (#591).
  if (i.type === 'syndic') {
    return true;
  }

  if (!ownerLikeWorkspaceType(i.type)) {
    return true;
  }
  if (i.workspaceOnboardingCompleted) {
    return true;
  }
  if (i.onboardingJustFinished) {
    return true;
  }
  return ['/onboarding'];
}

/**
 * Decision for `AgencyOnboardingGuard` (protects the top-level, full-screen
 * `/agency/onboarding` route — outside the app shell, so no left menu).
 *
 * Mirrors `resolveOnboardingAccess` for the agency case but applies on the
 * agency route itself:
 *  - non-agency workspaces have no business here → home;
 *  - the universal USER onboarding always runs first (only the FIRST time the
 *    human connects — `userProfileCompleted` short-circuits it afterwards);
 *  - an already-configured agency is bounced to its dashboard;
 *  - otherwise the wizard is shown.
 */
export function resolveAgencyOnboarding(i: OnboardingRoutingInput): OnboardingDecision {
  if (i.type !== 'agency') {
    return ['/'];
  }
  if (!i.userProfileCompleted && !i.relaunch) {
    return ['/onboarding', 'user-profile'];
  }
  if (i.workspaceOnboardingCompleted || i.onboardingJustFinished) {
    return ['/agency/dashboard'];
  }
  return true;
}
